import { AppShell as MantineAppShell, NavLink as MantineNavLink, ScrollArea as MantineScrollArea } from "@mantine/core";
import { IconLayoutSidebarLeftCollapse, IconLayoutSidebarLeftExpand } from "@tabler/icons-react";
import classNames from "classnames";
import * as React from "react";

import type { ElementProps } from "../primitives/style-props";
import { tokens } from "../tokens";

import styles from "./application-frame.module.css";
import { Tooltip } from "./feedback";
import { Menu } from "./overlays";

export const APPLICATION_FRAME_HEADER_HEIGHT = 60;
export const APPLICATION_FRAME_ACTION_CENTER_WIDTH = 350;
export const APPLICATION_FRAME_CONTEXT_PANEL_PORTAL_ID = "static-aside";
export const APPLICATION_FRAME_MOBILE_ACTIONS_PORTAL_ID = "mobile-menu-bar";

export interface ApplicationFrameProps {
  /** ShelfCycle's fixed top application region. */
  header: React.ReactNode;
  /** ShelfCycle's primary navigation region, present only while it is open. */
  navigation?: React.ReactNode;
  /** Whether the Action Center owns the desktop side rail. */
  isActionCenterOpen: boolean;
  /** The application content region. */
  children: React.ReactNode;
}

/**
 * ShelfCycle's application frame. Mantine's shell structure is a private adapter so an adapter
 * rewrite does not change callers' region or state contracts.
 *
 * Mantine 7 removed AppShell's `header`/`navbar`/`aside` element props and its body wrapper
 * (SC-2703): the regions are children, the header height is root configuration, and `frameBody`
 * is an owned element keeping Mantine 6's navigation → main → context-panel row. Mantine 6's
 * main padding offsets were always zeroed by `frameMain`, so `padding`/`navbarOffsetBreakpoint`
 * have no counterpart; `transitionDuration={0}` keeps Mantine 6's untransitioned geometry.
 */
export const ApplicationFrame = ({ header, navigation, isActionCenterOpen, children }: ApplicationFrameProps): React.JSX.Element => (
  <MantineAppShell
    className={styles.frame}
    data-action-center-open={isActionCenterOpen ? "true" : undefined}
    data-application-frame="true"
    header={{ height: APPLICATION_FRAME_HEADER_HEIGHT }}
    layout="alt"
    transitionDuration={0}
  >
    {header}
    <div className={styles.frameBody}>
      {navigation ? navigation : undefined}
      <MantineAppShell.Main className={styles.frameMain}>{children}</MantineAppShell.Main>
      <div className={styles.contextPanelHost} id={APPLICATION_FRAME_CONTEXT_PANEL_PORTAL_ID} />
    </div>
  </MantineAppShell>
);

export interface ApplicationFrameContentProps {
  /** Optional application-level notice rendered above mobile actions and page content. */
  banner?: React.ReactNode;
  /** The loading indicator positioned over the application content region. */
  loadingIndicator: React.ReactNode;
  children: React.ReactNode;
}

/** Owns the application notice region, scroll contents, loading placement, and mobile-action portal host. */
export const ApplicationFrameContent = ({ banner, loadingIndicator, children }: ApplicationFrameContentProps): React.JSX.Element => (
  <div className={styles.contentWrapper}>
    <div className={styles.loadingOverlay}>{loadingIndicator}</div>
    <div className={styles.content}>
      {banner}
      <div className={styles.mobileActions}>
        <div className={styles.mobileActionsHost} id={APPLICATION_FRAME_MOBILE_ACTIONS_PORTAL_ID} />
      </div>
      {children}
    </div>
  </div>
);

export interface ApplicationHeaderProps {
  children: React.ReactNode;
}

/**
 * ShelfCycle's fixed top chrome; its geometry is frame-owned rather than caller-configured. Mantine 7
 * moved the header height from the header onto ApplicationFrame's AppShell configuration (SC-2703).
 */
export const ApplicationHeader = ({ children }: ApplicationHeaderProps): React.JSX.Element => (
  <MantineAppShell.Header className={styles.applicationHeader} p={0} pl="xs" pr="sm" style={{ zIndex: 3 }}>
    <div className={styles.headerContent}>{children}</div>
  </MantineAppShell.Header>
);

export interface PrimaryNavigationProps {
  /** Whether the ShelfCycle primary navigation is in its compact 60px state. */
  collapsed: boolean;
  children: React.ReactNode;
}

const NavigationCollapseContext = React.createContext(false);

const NAVIGATION_SLIDE_TIMING: KeyframeAnimationOptions = {
  duration: 200,
  easing: "cubic-bezier(0.2, 0, 0, 1)",
  fill: "forwards",
};

const isHTMLElement = (element: Element): element is HTMLElement => element instanceof HTMLElement;

const slide = (element: HTMLElement, from: number, to: number): Animation =>
  element.animate([{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }], NAVIGATION_SLIDE_TIMING);

/**
 * Slides the navigation open or closed when its width changes after the first paint, with transforms only, so a long
 * page render cannot stall it. The navigation's clipping box moves while its sections move the opposite way, so the
 * contents hold still as the edge slides; while collapsing it keeps its wider width (a matching negative end margin keeps
 * its outer width final) until the slide ends. The main column and header lay out once at their final position and
 * slide into it. Nothing stays applied afterwards. No slide on mount, below the desktop breakpoint or under reduced motion.
 */
const useNavigationSlide = (collapsed: boolean): React.RefObject<HTMLElement> => {
  const navigationRef = React.useRef<HTMLElement>(null);
  const settledWidthRef = React.useRef<number | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: runs on a collapsed change; the width comes from the element
  React.useLayoutEffect(() => {
    const navigation = navigationRef.current;
    if (!navigation) {
      return;
    }
    const width = navigation.getBoundingClientRect().width;
    const previousWidth = settledWidthRef.current;
    settledWidthRef.current = width;
    if (
      previousWidth === null ||
      previousWidth === width ||
      typeof navigation.animate !== "function" ||
      typeof window.matchMedia !== "function" ||
      !window.matchMedia(`(min-width: ${tokens.breakpoint.md})`).matches ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    const frameBody = navigation.parentElement;
    const header = frameBody?.parentElement?.querySelector<HTMLElement>(`.${styles.applicationHeader}`);
    const main = frameBody?.querySelector<HTMLElement>(`.${styles.frameMain}`);
    // Positive while collapsing, negative while expanding.
    const offset = previousWidth - width;
    const collapsing = offset > 0;
    const sections = Array.from(navigation.children).filter(isHTMLElement);
    sections.forEach(section => {
      section.style.width = `${section.getBoundingClientRect().width}px`;
    });
    navigation.style.overflow = "hidden";
    if (collapsing) {
      navigation.style.width = `${previousWidth}px`;
      navigation.style.minWidth = `${previousWidth}px`;
      navigation.style.marginInlineEnd = `${-offset}px`;
    }

    // Expanding reveals the wider navigation from its final 60px edge; collapsing hides it down to that edge.
    const animations = [
      collapsing ? slide(navigation, 0, -offset) : slide(navigation, offset, 0),
      ...sections.map(section => (collapsing ? slide(section, 0, offset) : slide(section, -offset, 0))),
      ...[header, main].flatMap(element => (element ? [slide(element, offset, 0)] : [])),
    ];
    let settled = false;
    const settle = () => {
      if (settled) {
        return;
      }
      settled = true;
      sections.forEach(section => section.style.removeProperty("width"));
      ["overflow", "width", "min-width", "margin-inline-end"].forEach(property => navigation.style.removeProperty(property));
      // Same task as the style reset, so no frame shows the forwards fill without it.
      animations.forEach(animation => animation.cancel());
    };
    animations[0]?.addEventListener("finish", settle);

    return settle;
  }, [collapsed]);

  return navigationRef;
};

/**
 * ShelfCycle's 200px/60px primary navigation region. Mantine 7's navbar has no `width` prop and its
 * root configuration cannot see `collapsed`, so the Mantine 6 widths live in the module CSS (SC-2703).
 */
export const PrimaryNavigation = ({ collapsed, children }: PrimaryNavigationProps): React.JSX.Element => {
  const navigationRef = useNavigationSlide(collapsed);

  return (
    <MantineAppShell.Navbar
      bg={tokens.color.surface.canvas}
      className={classNames(styles.primaryNavigation, collapsed && styles.compactNavigation)}
      pt={{ base: APPLICATION_FRAME_HEADER_HEIGHT, md: 0 }}
      ref={navigationRef}
      style={{ zIndex: 2 }}
    >
      <NavigationCollapseContext.Provider value={collapsed}>{children}</NavigationCollapseContext.Provider>
    </MantineAppShell.Navbar>
  );
};

export interface PrimaryNavigationSectionProps {
  /** Gives the section the navigation rail's remaining scrollable space. */
  scrollable?: boolean;
  /** The scrolling viewport of a `scrollable` section, for callers that reset its scroll position. */
  viewportRef?: React.Ref<HTMLDivElement>;
  children: React.ReactNode;
}

/** A fixed or scroll-owning section within ShelfCycle's primary navigation. */
export const PrimaryNavigationSection = ({
  scrollable = false,
  viewportRef,
  children,
}: PrimaryNavigationSectionProps): React.JSX.Element => {
  if (!scrollable) {
    return <MantineAppShell.Section className={styles.navigationSection}>{children}</MantineAppShell.Section>;
  }

  return (
    <MantineAppShell.Section
      className={`${styles.navigationSection} ${styles.scrollableNavigationSection}`}
      component={MantineScrollArea}
      grow
      px={0}
      viewportRef={viewportRef}
    >
      {children}
    </MantineAppShell.Section>
  );
};

type PrimaryNavigationLinkComponent = (props: { href: string; children?: React.ReactNode }) => React.ReactNode;

type PrimaryNavigationLinkTarget =
  | {
      href: string;
      linkComponent: PrimaryNavigationLinkComponent;
    }
  | {
      href?: never;
      linkComponent?: never;
    };

export type PrimaryNavigationLinkProps = {
  label?: React.ReactNode;
  icon?: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** Muted navigation actions such as the settings return control. */
  tone?: "default" | "muted";
  className?: string;
} & PrimaryNavigationLinkTarget;

/** A ShelfCycle navigation action; compact icon spacing is resolved from navigation state internally. */
export const PrimaryNavigationLink = (props: PrimaryNavigationLinkProps): React.JSX.Element => {
  const collapsed = React.useContext(NavigationCollapseContext);
  // Mantine 7 declares NavLink's handler for its anchor root; Mantine 6 declared none, so a link component
  // already received the anchor's event through the owned button-typed handler. Widening to the element
  // base type (React handlers are bivariant) keeps that runtime delivery without a cast (SC-2703).
  const onClick: React.MouseEventHandler<HTMLElement> | undefined = props.onClick;
  const commonProps = {
    active: props.active,
    className: props.className,
    // Mantine 7 renamed NavLink's `icon` prop and slot to `leftSection`/`section`; the owned names are unchanged.
    classNames: collapsed ? { section: styles.compactNavigationLinkIcon } : undefined,
    color: props.tone === "muted" ? "gray" : undefined,
    disabled: props.disabled,
    label: props.label,
    leftSection: props.icon,
    onClick,
  };

  if (props.linkComponent) {
    return <MantineNavLink {...commonProps} component={props.linkComponent} href={props.href} />;
  }

  // Mantine 6 rendered a natively disabled `<button type="button">`; Mantine 7 keeps `disabled` as a data
  // attribute only, so the button root carries the native attribute here (SC-2703).
  return (
    <MantineNavLink {...commonProps} component="button" renderRoot={rootProps => <button {...rootProps} disabled={props.disabled} />} />
  );
};

/** Opens on keyboard focus as well as hover, so a keyboard user hears and sees an icon's name. */
const NAVIGATION_TOOLTIP_EVENTS = { hover: true, focus: true, touch: false };
/** Clears the focus ring, with an arrow large enough to read as attached to its control. */
const NAVIGATION_TOOLTIP_OFFSET = 12;
const NAVIGATION_TOOLTIP_ARROW_SIZE = 6;

export type PrimaryNavigationButtonProps = ElementProps<"button"> & {
  label: string;
  icon: React.ReactNode;
  /** Marks the button as the place the user is in. */
  current?: boolean;
  /** Suppresses the compact name tooltip, for example while the button's own menu is open. */
  tooltipDisabled?: boolean;
};

/**
 * A navigation action that is a button, not a page link: a full-width row with its label in the open
 * navigation, a named icon tile in the compact one. The same element renders in both, so focus survives
 * the navigation changing state.
 */
export const PrimaryNavigationButton = React.forwardRef<HTMLButtonElement, PrimaryNavigationButtonProps>(function PrimaryNavigationButton(
  { label, icon, current = false, tooltipDisabled = false, className, ...props },
  ref,
) {
  const collapsed = React.useContext(NavigationCollapseContext);

  return (
    <Tooltip
      arrowSize={NAVIGATION_TOOLTIP_ARROW_SIZE}
      disabled={!collapsed || tooltipDisabled}
      events={NAVIGATION_TOOLTIP_EVENTS}
      label={label}
      offset={NAVIGATION_TOOLTIP_OFFSET}
      position="right"
      withArrow
    >
      <button
        {...props}
        aria-current={current ? "true" : undefined}
        aria-label={collapsed ? label : undefined}
        className={classNames(collapsed ? styles.navigationTile : styles.navigationRow, className)}
        data-current={current ? "true" : undefined}
        ref={ref}
        type="button"
      >
        {icon}
        {collapsed ? undefined : <span className={styles.navigationRowLabel}>{label}</span>}
      </button>
    </Tooltip>
  );
});

export interface PrimaryNavigationRailGroupProps {
  label: string;
  icon: React.ReactNode;
  /** Whether the group holds the current page. */
  current: boolean;
  children: React.ReactNode;
}

/**
 * One group of the compact navigation: an icon tile whose click opens the group's pages in a menu beside it.
 * Only one menu is ever open, because clicking another tile is an outside click for the first.
 */
export const PrimaryNavigationRailGroup = ({ label, icon, current, children }: PrimaryNavigationRailGroupProps): React.JSX.Element => {
  const [opened, setOpened] = React.useState(false);

  return (
    <div className={styles.railRow}>
      <Menu
        dropdownClassName={styles.railMenu}
        itemClassName={styles.railMenuItem}
        offset={{ mainAxis: 18, alignmentAxis: -6 }}
        onClose={() => setOpened(false)}
        onOpen={() => setOpened(true)}
        position="right-start"
        shadow="md"
        width={216}
        withinPortal
      >
        <Menu.Target>
          <PrimaryNavigationButton
            current={current}
            icon={icon}
            label={label}
            // Safari does not focus a clicked button. Focusing it before the menu opens lets the menu's
            // Escape return focus to the tile in every browser; the menu target's own toggle runs after this.
            onClick={event => event.currentTarget.focus()}
            tooltipDisabled={opened}
          />
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label className={styles.railMenuLabel}>{label}</Menu.Label>
          {children}
        </Menu.Dropdown>
      </Menu>
    </div>
  );
};

export interface PrimaryNavigationRailMenuLinkProps {
  label: React.ReactNode;
  href: string;
  linkComponent: PrimaryNavigationLinkComponent;
  active: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

/** A page inside a compact navigation group's menu. */
export const PrimaryNavigationRailMenuLink = ({
  label,
  href,
  linkComponent,
  active,
  onClick,
}: PrimaryNavigationRailMenuLinkProps): React.JSX.Element => (
  <Menu.Item
    aria-current={active ? "page" : undefined}
    component={linkComponent}
    data-active={active ? "true" : undefined}
    href={href}
    onClick={onClick}
  >
    {label}
  </Menu.Item>
);

export interface PrimaryNavigationFooterProps {
  children?: React.ReactNode;
}

/** The fixed actions at the bottom of the navigation: a row in the open navigation, a column in the compact one. */
export const PrimaryNavigationFooter = ({ children }: PrimaryNavigationFooterProps): React.JSX.Element | null => {
  const collapsed = React.useContext(NavigationCollapseContext);

  if (React.Children.toArray(children).length === 0) {
    return null;
  }

  return (
    <PrimaryNavigationSection>
      <div className={classNames(styles.navigationFooter, collapsed && styles.compactNavigationFooter)}>{children}</div>
    </PrimaryNavigationSection>
  );
};

export interface PrimaryNavigationCollapseButtonProps {
  collapsed: boolean;
  onToggle: () => void;
}

/** Collapses or expands the navigation; its tooltip teaches the `[` key that does the same. */
export const PrimaryNavigationCollapseButton = ({ collapsed, onToggle }: PrimaryNavigationCollapseButtonProps): React.JSX.Element => {
  const label = collapsed ? "Expand sidebar" : "Collapse sidebar";
  const Icon = collapsed ? IconLayoutSidebarLeftExpand : IconLayoutSidebarLeftCollapse;

  return (
    <Tooltip
      arrowSize={NAVIGATION_TOOLTIP_ARROW_SIZE}
      events={NAVIGATION_TOOLTIP_EVENTS}
      label={
        <span className={styles.shortcutLabel}>
          {label}
          <kbd className={styles.shortcutKey}>[</kbd>
        </span>
      }
      offset={NAVIGATION_TOOLTIP_OFFSET}
      position="right"
      withArrow
    >
      <button
        aria-keyshortcuts="["
        aria-label={label}
        className={classNames(styles.collapseButton, collapsed && styles.compactCollapseButton)}
        onClick={onToggle}
        type="button"
      >
        <Icon size={20} stroke={1.5} />
      </button>
    </Tooltip>
  );
};

export interface ContextPanelProps {
  children: React.ReactNode;
}

/**
 * ShelfCycle's fixed-width comments and documents region. Mantine 6's `p="md"` moved to the module CSS:
 * Mantine 7 style props are inline and would beat the desktop header offset. `zIndex` keeps Mantine 6's
 * default app layer, which Mantine 7's aside raises by one (SC-2703).
 */
export const ContextPanel = ({ children }: ContextPanelProps): React.JSX.Element => (
  <MantineAppShell.Aside className={styles.contextPanel} style={{ zIndex: 100 }} w={300}>
    {children}
  </MantineAppShell.Aside>
);

export interface MobileOnlyProps {
  children: React.ReactNode;
}

/** ShelfCycle navigation controls that are visible only below the desktop application breakpoint. */
export const MobileOnly = ({ children }: MobileOnlyProps): React.JSX.Element => <div className={styles.mobileOnly}>{children}</div>;
